/**
 * Web React Query hooks — ported subset of mobile's queries file. Keeps
 * the two clients in sync without yet extracting a `packages/api-client`.
 * Mobile lives at `apps/isale-mobile/src/shared/api/queries.ts` — if you
 * change a shape here, mirror it there (or vice-versa).
 */
import { useRef } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api, sessionStore } from "./client";

/**
 * CF-H3: after the script changes (regenerate, or select a different
 * variant) the cached scene plan was built from the OLD script — and the
 * project's current_script_version_id / selected_variant_index just moved.
 * Invalidate all three so the editor re-decides POST-vs-PATCH against fresh
 * data and the next render uses voiceover for the NEW script, not stale audio.
 */
function invalidateScriptDependents(
  qc: QueryClient,
  projectId: string | undefined,
): void {
  qc.invalidateQueries({ queryKey: ["scripts", projectId] });
  qc.invalidateQueries({ queryKey: ["scene-plan", projectId] });
  qc.invalidateQueries({ queryKey: ["project", projectId] });
}

// Adaptive polling tunables — shared by useJob (and reused by mobile).
// Base ≤ 1s gives snappy feel while progress changes; after STABLE_THRESHOLD
// polls with no change we slow to SLOW_INTERVAL_MS to cut request volume
// (~3× fewer reqs once render plateaus on FFmpeg).
const POLL_STABLE_THRESHOLD = 10;
const POLL_SLOW_INTERVAL_MS = 3000;

// ───────── Auth ─────────

export interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
    display_name: string | null;
    default_workspace_id: string;
  };
}

export function useGoogleLogin() {
  return useMutation<
    AuthResponse,
    Error,
    { id_token: string; locale?: "vi" | "en" | "zh" }
  >({
    mutationFn: (vars) =>
      api.post("/v1/auth/google/login", vars, { skipAuth: true }),
  });
}

export function useAppleLogin() {
  return useMutation<
    AuthResponse,
    Error,
    { id_token: string; locale?: "vi" | "en" | "zh" }
  >({
    mutationFn: (vars) =>
      api.post("/v1/auth/apple/login", vars, { skipAuth: true }),
  });
}

export function useAcceptConsent() {
  return useMutation<
    { consent_version: string; accepted_at: string },
    Error,
    { consent_version: string }
  >({
    mutationFn: (vars) => api.post("/v1/consents/accept", vars),
  });
}

// ───────── Users / profile ─────────

/** B6 / V026 — server-resolved with defaults; all 7 keys always present. */
export interface NotifPrefs {
  renderDone: boolean;
  renderFailed: boolean;
  renderReady: boolean;
  quotaWarn: boolean;
  quotaFull: boolean;
  quotaPromo: boolean;
  emailWeekly: boolean;
}

export interface UserMe {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  // D4 W-P-01 (V025 backend migration). All nullable — pre-V025 rows return null.
  phone: string | null;
  tiktok_handle: string | null;
  bio_shop: string | null;
  // B6 / V026 — 7-boolean blob from `users.notif_prefs` JSONB column.
  notif_prefs: NotifPrefs;
  locale: "vi" | "en" | "zh";
  created_at: string;
  updated_at: string;
}

export function useUserMe() {
  return useQuery<UserMe>({
    queryKey: ["user", "me"],
    queryFn: () => api.get<UserMe>("/v1/users/me"),
    // FL-12: skip the request when there's no session — the unauthenticated
    // call would return 401 every time, spamming the console on logged-out
    // page loads. The layout auth-guard handles the redirect; `enabled`
    // prevents the noise from the transient pre-redirect render.
    enabled: !!sessionStore.load(),
  });
}

/**
 * P1 — soft-delete current account (V027 backend). Returns the
 * deletion timestamp + when the row is eligible for hard-delete + how
 * many refresh tokens got revoked. After success the caller MUST:
 *   1. sessionStore.clear() (kill local auth)
 *   2. navigate("/sign-in") (or trigger AuthExpiredModal flow)
 * Existing access tokens stay valid until natural expiry (max 15 min)
 * but refresh is now blocked, so the next refresh attempt will 401.
 */
export function useDeleteAccount() {
  return useMutation<
    { deleted_at: string; purge_after: string; revoked_sessions: number },
    Error,
    void
  >({
    mutationFn: () => api.delete<{
      deleted_at: string;
      purge_after: string;
      revoked_sessions: number;
    }>("/v1/users/me"),
  });
}

export function useUpdateUserMe() {
  const qc = useQueryClient();
  return useMutation<
    UserMe,
    Error,
    {
      display_name?: string | null;
      avatar_url?: string | null;
      locale?: "vi" | "en" | "zh";
      // D4 W-P-01 profile fields. Send empty string to clear; omit to keep.
      phone?: string | null;
      tiktok_handle?: string | null;
      bio_shop?: string | null;
      // B6 / V026 — partial-merge on the server; only send the keys you
      // want to change. Server overlays them on top of the stored blob.
      notif_prefs?: Partial<NotifPrefs>;
    }
  >({
    mutationFn: (vars) => api.patch<UserMe>("/v1/users/me", vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user", "me"] }),
  });
}

export interface AvatarUploadUrl {
  upload_url: string;
  key: string;
  expires_at: string;
}

/** Request a presigned R2 PUT URL for the current user's avatar upload. */
export function useRequestAvatarUploadUrl() {
  return useMutation<AvatarUploadUrl, Error, { content_type: string }>({
    mutationFn: (vars) =>
      api.post<AvatarUploadUrl>("/v1/users/me/avatar/upload-url", vars),
  });
}

// ───────── Product image upload (MP14) ─────────

/** Per-file spec we send to /v1/products/{id}/upload-url. */
export interface UploadFileSpec {
  filename: string;
  mime: "image/jpeg" | "image/png" | "image/webp";
  size_bytes: number;
  sha256: string;
}

/** One presigned PUT slot returned by the Worker. */
export interface UploadSlot {
  asset_id: string;
  url: string;
  method: "PUT";
  headers: Record<string, string>;
  expires_at: string;
}

export interface RequestUploadUrlsResponse {
  uploads: UploadSlot[];
}

export interface ProductAsset {
  id: string;
  product_id: string;
  s3_key: string;
  /** Signed 1-hour R2 GET URL, suitable for <img src>. Null if R2 sign failed. */
  url: string | null;
  kind: "image" | "video" | "audio";
  mime: string;
  size_bytes: number;
  sha256: string;
  moderation_status: "pending" | "approved" | "rejected";
  moderation_reason: string | null;
  created_at: string;
}

export interface ProductDetail {
  id: string;
  workspace_id: string;
  name: string;
  category: string;
  status: "draft" | "ready" | "archived";
  cover_url: string | null;
  created_at: string;
  updated_at: string;
  assets: ProductAsset[];
}

export interface UploadConfirmResponse {
  asset: ProductAsset;
  product_status: "draft" | "ready" | "archived";
}

/**
 * Request N presigned PUT URLs for product images. Backend caps at
 * MAX_UPLOADS_PER_REQUEST=10. Caller PUTs each file to the returned `url`
 * with the matching `headers` (Sigv4 sensitive), then POSTs the
 * confirm endpoint per asset.
 */
export function useRequestProductUploadUrls(productId: string | null | undefined) {
  return useMutation<
    RequestUploadUrlsResponse,
    Error,
    { files: UploadFileSpec[] }
  >({
    mutationFn: (vars) => {
      if (!productId) throw new Error("productId required");
      return api.post<RequestUploadUrlsResponse>(
        `/v1/products/${productId}/upload-url`,
        vars,
      );
    },
  });
}

/**
 * Confirm a single uploaded asset — Worker runs moderation inline. May
 * return 422 if rejected; we surface that as a normal error and let the
 * UI flip the tile to "rejected" state.
 */
export function useConfirmProductUpload(productId: string | null | undefined) {
  return useMutation<UploadConfirmResponse, Error, { asset_id: string }>({
    mutationFn: (vars) => {
      if (!productId) throw new Error("productId required");
      return api.post<UploadConfirmResponse>(
        `/v1/products/${productId}/assets/${vars.asset_id}/uploaded`,
        {},
      );
    },
  });
}

/** Read product detail incl. `assets[]`. */
export function useProductDetail(productId: string | null | undefined) {
  return useQuery<ProductDetail>({
    queryKey: ["product", productId],
    enabled: !!productId,
    queryFn: () => api.get<ProductDetail>(`/v1/products/${productId}`),
  });
}

/**
 * MP14 U-14 — DELETE a single product asset (DB row + R2 object).
 *
 * Used by the cancel-during-upload flow in `MultiFileDropzone.tsx` to
 * clean up assets the user discarded. The backend endpoint is
 * idempotent (returns 204 even when the asset never existed) so the
 * client can call this for every queued/uploaded asset_id in the batch
 * without coordinating which ones actually made it past confirm.
 */
export function useDeleteProductAsset(productId: string | null | undefined) {
  return useMutation<void, Error, { asset_id: string }>({
    mutationFn: (vars) => {
      if (!productId) throw new Error("productId required");
      return api.delete<void>(
        `/v1/products/${productId}/assets/${vars.asset_id}`,
      );
    },
  });
}

// ───────── GA-X-04: Archive (soft-delete) a product ─────────

/**
 * Soft-delete a product by calling DELETE /v1/products/:id.
 * Backend sets status="archived" and returns 204 — no body.
 * Invalidates the products list so ProductsPage reflects the change
 * immediately. Caller is responsible for navigating away on success.
 */
export function useDeleteProduct(productId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => {
      if (!productId) throw new Error("productId required");
      return api.delete<void>(`/v1/products/${productId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["product", productId] });
    },
  });
}

// ───────── Facts (W-12 Fact Locker, mirrors mobile queries.ts) ─────────

export type FactSource = "vision_inference" | "user_input" | "user_verified";

export interface ProductFact {
  id: string;
  product_id: string;
  field: string;
  value: unknown;
  source: FactSource;
  confidence: number | null;
  is_regulated: boolean;
  verified_by: string | null;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useFacts(productId: string | undefined) {
  return useQuery<{ items: ProductFact[]; total: number }>({
    queryKey: ["facts", productId],
    queryFn: () =>
      api.get<{ items: ProductFact[]; total: number }>(
        `/v1/products/${productId}/facts`,
      ),
    enabled: !!productId,
  });
}

export function useCreateFact(productId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<ProductFact, Error, { field: string; value: unknown }>({
    mutationFn: (vars) =>
      api.post<ProductFact>(`/v1/products/${productId}/facts`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["facts", productId] }),
  });
}

export function usePatchFact(productId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    ProductFact,
    Error,
    { factId: string; value?: unknown; verified?: boolean }
  >({
    mutationFn: ({ factId, ...body }) =>
      api.patch<ProductFact>(`/v1/products/${productId}/facts/${factId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["facts", productId] }),
  });
}

export function useDeleteFact(productId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<void, Error, { factId: string }>({
    mutationFn: ({ factId }) =>
      api.delete<void>(`/v1/products/${productId}/facts/${factId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["facts", productId] }),
  });
}

// ───────── Notifications (TASK 2A/2B + web inbox MP14-followup) ─────────

export type NotificationType =
  | "render_done"
  | "render_failed"
  | "quota_warn"
  | "payment_failed";

export interface NotificationRow {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  deep_link: string | null;
  read_at: string | null;
  created_at: string;
}

export interface NotificationListResponse {
  items: NotificationRow[];
  unread_count: number;
  has_more: boolean;
}

/**
 * Inbox list query. Polls every 30s by default so the header bell badge
 * stays in sync without WebSockets. `unread_only=true` gives the small
 * dataset used by the bell popover; full inbox lists everything.
 */
export function useNotifications(
  opts: { unreadOnly?: boolean; limit?: number } = {},
) {
  const limit = opts.limit ?? 20;
  const unreadOnly = opts.unreadOnly ?? false;
  return useQuery<NotificationListResponse>({
    queryKey: ["notifications", { unreadOnly, limit }],
    queryFn: () =>
      api.get<NotificationListResponse>(`/v1/notifications`, {
        unread_only: unreadOnly ? "true" : "false",
        limit: String(limit),
      }),
    // P3.6 — Pause the 30s polling cadence when the browser tab is
    // hidden so backgrounded tabs don't keep waking the Cloudflare
    // Worker every 30s for nothing. TanStack Query's
    // `refetchIntervalInBackground` defaults to `false`, but TS+TQ
    // also accept a function form for `refetchInterval` so we can
    // make the intent explicit + couple it with the visibility check
    // it already implements internally (interval pauses on
    // `document.visibilityState !== "visible"`). When the user comes
    // back, `refetchOnWindowFocus` (default true) triggers an
    // immediate refresh so the badge is up-to-date as soon as they
    // glance at it.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}

export function useReadNotification() {
  const qc = useQueryClient();
  return useMutation<{ id: string; read_at: string }, Error, { id: string }>({
    mutationFn: (vars) =>
      api.post(`/v1/notifications/${vars.id}/read`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useReadAllNotifications() {
  const qc = useQueryClient();
  return useMutation<{ marked: number }, Error, void>({
    mutationFn: () => api.post(`/v1/notifications/read-all`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

// ───────── Workspace / quota ─────────

export interface QuotaSnapshot {
  workspace_id: string;
  plan_tier: "free" | "starter" | "pro" | "enterprise";
  video_used: number;
  video_cap: number;
  ai_mode_used: number;
  ai_mode_cap: number;
  cost_usd_used: string;
  cost_cap_usd: number;
  period_start: string;
  period_end: string;
}

export function useQuota(workspaceId: string | undefined) {
  return useQuery<QuotaSnapshot>({
    queryKey: ["quota", workspaceId],
    queryFn: () => api.get(`/v1/workspaces/${workspaceId}/quota`),
    enabled: !!workspaceId,
  });
}

// ───────── Products ─────────

export interface ProductSummary {
  id: string;
  workspace_id: string;
  name: string;
  category: string;
  status: "draft" | "ready" | "archived";
  /** Signed 1-hour R2 GET URL of first approved asset, null if none yet. */
  cover_url: string | null;
  created_at: string;
  updated_at: string;
}

export function useProducts(workspaceId: string | undefined) {
  return useQuery<{ items: ProductSummary[]; total: number }>({
    queryKey: ["products", workspaceId],
    queryFn: () => api.get(`/v1/products`, { workspace_id: workspaceId }),
    enabled: !!workspaceId,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation<
    ProductSummary,
    Error,
    { workspace_id: string; name: string; category: string }
  >({
    mutationFn: (vars) => api.post("/v1/products", vars),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["products", vars.workspace_id] }),
  });
}

// ───────── Projects + Scripts ─────────

export interface ProjectSummary {
  id: string;
  workspace_id: string;
  product_id: string;
  name: string;
  status: "draft" | "ready" | "archived" | "rendered";
  target_duration_s: number;
  template_id: string | null;
  current_script_version_id: string | null;
  created_at: string;
  updated_at: string;
}

export function useProjects(workspaceId: string | undefined) {
  return useQuery<{ items: ProjectSummary[]; total: number }>({
    queryKey: ["projects", workspaceId],
    queryFn: () => api.get(`/v1/projects`, { workspace_id: workspaceId }),
    enabled: !!workspaceId,
  });
}

export interface ProjectDetail extends ProjectSummary {
  product: {
    id: string;
    name: string;
    category: string;
    status: string;
  } | null;
  script_versions: Array<{
    id: string;
    version: number;
    selected_variant_index: number | null;
    created_at: string;
  }>;
}

export function useProject(projectId: string | undefined) {
  return useQuery<ProjectDetail>({
    queryKey: ["project", projectId],
    queryFn: () => api.get(`/v1/projects/${projectId}`),
    enabled: !!projectId,
  });
}

/**
 * Create a new project (clip) against a `status=ready` product. Mirror
 * of `apps/isale-mobile/src/shared/api/queries.ts::useCreateProject`.
 *
 * Backend requires the chosen product to be in `ready` status (412
 * otherwise). The Clip empty-state CTA on the web filters its product
 * dropdown to ready-only so this 412 should rarely fire in practice,
 * but the modal still surfaces the error message if it does.
 */
export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation<
    ProjectSummary,
    Error,
    {
      workspace_id: string;
      product_id: string;
      name: string;
      target_duration_s?: number;
    }
  >({
    mutationFn: (vars) => api.post<ProjectSummary>("/v1/projects", vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["projects"] }),
  });
}

/** Script format catalog (multi-vertical F1.8) — drives the P1 clip-flow
 *  template picker. Backend `generate-creative` already accepts these. */
export type ScriptFormat =
  | "review"
  | "unboxing"
  | "before_after"
  | "demo"
  | "story"
  | "promotion"
  | "testimonial"
  | "faq";

export function useGenerateCreative(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    ScriptVersion,
    Error,
    {
      tone?: "casual" | "professional" | "playful" | "trustworthy";
      locale?: "vi" | "en";
      format?: ScriptFormat;
    }
  >({
    mutationFn: (vars) =>
      api.post<ScriptVersion>(`/v1/projects/${projectId}/generate-creative`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scripts", projectId] }),
  });
}

export interface ScriptScene {
  index: number;
  visual_direction: string;
  voiceover: string;
  duration_s: number;
}

export interface ScriptVariant {
  variant_index: number;
  hook: string;
  scenes: ScriptScene[];
  cta: string;
  hashtags: string[];
}

export interface ComplianceWarning {
  rule_id: string;
  category: "cosmetic" | "supplement" | "pharma" | "food" | "generic";
  severity: "info" | "warning" | "critical";
  snippet: string;
  reason: string;
}

export interface ScriptVersion {
  id: string;
  project_id: string;
  creative_brief_id: string;
  version: number;
  variants: ScriptVariant[];
  compliance_warnings: ComplianceWarning[];
  selected_variant_index: number | null;
  provider: string | null;
  model: string | null;
  created_at: string;
}

export function useScripts(projectId: string | undefined) {
  return useQuery<{ items: ScriptVersion[]; total: number }>({
    queryKey: ["scripts", projectId],
    queryFn: () => api.get(`/v1/projects/${projectId}/scripts`),
    enabled: !!projectId,
  });
}

export function useRegenerateScript(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    ScriptVersion,
    Error,
    { feedback?: string; target_variant_index?: number }
  >({
    mutationFn: (vars) =>
      api.post(`/v1/projects/${projectId}/scripts/regenerate`, vars),
    onSuccess: () => invalidateScriptDependents(qc, projectId),
  });
}

export function useSelectScript(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    ScriptVersion,
    Error,
    { versionId: string; variant_index: number }
  >({
    mutationFn: ({ versionId, variant_index }) =>
      api.post(`/v1/projects/${projectId}/scripts/${versionId}/select`, {
        variant_index,
      }),
    onSuccess: () => invalidateScriptDependents(qc, projectId),
  });
}

export interface CaptionResponse {
  caption: string;
  hashtags: string[];
  based_on_script_version_id: string;
  based_on_variant_index: number;
  provider: string;
  model: string;
}

export function useGenerateCaption(projectId: string | undefined) {
  return useMutation<CaptionResponse, Error, void>({
    mutationFn: () => api.post(`/v1/projects/${projectId}/caption`),
  });
}

// ───────── Media catalogs ─────────

export interface VoiceItem {
  voice_id: string;
  display_name: string;
  locale: "vi" | "en";
  gender: "female" | "male";
}

export interface MusicItem {
  music_id: string;
  display_name: string;
  mood: "upbeat" | "chill" | "cinematic" | "playful" | "trustworthy";
  duration_s: number;
  bpm: number;
  license: "pixabay" | "fma-cc0";
}

export interface TemplateItem {
  template_id: string;
  display_name: string;
  aspect: "9:16" | "1:1" | "16:9";
  scene_transition: "cut" | "fade" | "ken_burns";
  description: string;
}

export interface SubtitleStyleItem {
  style_id: string;
  display_name: string;
  font_family: string;
  description?: string;
}

export function useVoices(locale: "vi" | "en" = "vi") {
  return useQuery<{ items: VoiceItem[] }>({
    queryKey: ["voices", locale],
    queryFn: () => api.get(`/v1/voices`, { locale }),
    staleTime: 60 * 60 * 1000,
  });
}

export function useMusic() {
  return useQuery<{ items: MusicItem[] }>({
    queryKey: ["music"],
    queryFn: () => api.get(`/v1/music`),
    staleTime: 60 * 60 * 1000,
  });
}

export function useTemplates(aspect: "9:16" | "1:1" | "16:9" = "9:16") {
  return useQuery<{ items: TemplateItem[] }>({
    queryKey: ["templates", aspect],
    queryFn: () => api.get(`/v1/templates`, { aspect }),
    staleTime: 60 * 60 * 1000,
  });
}

export function useSubtitleStyles() {
  return useQuery<{ items: SubtitleStyleItem[] }>({
    queryKey: ["subtitle-styles"],
    queryFn: () => api.get(`/v1/subtitle-styles`),
    staleTime: 60 * 60 * 1000,
  });
}

// ───────── Scene plan ─────────

export interface ScenePlanScene {
  scene_index: number;
  image_asset_id: string | null;
  voiceover_text: string;
  voiceover_audio: {
    bucket: string;
    s3_key: string;
    duration_s: number;
    cache_hit: boolean;
  };
  visual_direction: string;
  duration_s: number;
}

export interface ScenePlan {
  id: string;
  project_id: string;
  script_version_id: string;
  selected_variant_index: number;
  template_id: string;
  voice_id: string;
  music_id: string | null;
  subtitle_style: string;
  engine: "fast" | "ai_hybrid";
  plan: {
    total_duration_s: number;
    scenes: ScenePlanScene[];
  };
  estimated_cost_usd: string;
  created_at: string;
  updated_at: string;
}

export function useScenePlan(projectId: string | undefined) {
  return useQuery<ScenePlan>({
    queryKey: ["scene-plan", projectId],
    queryFn: () => api.get(`/v1/projects/${projectId}/scene-plan`),
    enabled: !!projectId,
    retry: false,
  });
}

export function useCreateScenePlan(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    ScenePlan,
    Error,
    {
      template_id: string;
      voice_id: string;
      music_id?: string | null;
      subtitle_style?: string;
      engine?: "fast" | "ai_hybrid";
    }
  >({
    mutationFn: (vars) => api.post(`/v1/projects/${projectId}/scene-plan`, vars),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["scene-plan", projectId] }),
  });
}

export function usePatchScenePlan(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    ScenePlan,
    Error,
    {
      template_id?: string;
      music_id?: string | null;
      subtitle_style?: string;
      engine?: "fast" | "ai_hybrid";
    }
  >({
    mutationFn: (vars) =>
      api.patch(`/v1/projects/${projectId}/scene-plan`, vars),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["scene-plan", projectId] }),
  });
}

// ───────── Render jobs ─────────

export interface JobDetail {
  id: string;
  type: "render_preview" | "render_final";
  workspace_id: string;
  project_id: string;
  status: "queued" | "running" | "success" | "failed" | "cancelled";
  step_name: string | null;
  progress_pct: number;
  payload: Record<string, unknown>;
  result: Record<string, unknown> | null;
  cost_usd_actual: string;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobAck {
  id: string;
  type: "render_preview" | "render_final";
  status: "queued" | "running" | "success" | "failed" | "cancelled";
  status_url: string;
  events_url: string;
  estimated_completion_s: number;
}

/**
 * CF-H1: render POST carries a caller-minted idempotency-key (UUID) so a
 * double-tap / retry-after-blip / remount can't enqueue two render jobs (=
 * double Modal spend + double quota burn). The backend (render.ts) returns
 * the existing job verbatim for a repeated key. The Render screen holds the
 * key in a ref per render attempt and rotates it on retry.
 */
export function useRenderPreview(projectId: string | undefined) {
  return useMutation<JobAck, Error, { idempotencyKey: string }>({
    mutationFn: ({ idempotencyKey }) =>
      api.post(`/v1/projects/${projectId}/render-preview`, undefined, {
        headers: { "idempotency-key": idempotencyKey },
      }),
  });
}

export function useRenderFinal(projectId: string | undefined) {
  return useMutation<JobAck, Error, { idempotencyKey: string }>({
    mutationFn: ({ idempotencyKey }) =>
      api.post(`/v1/projects/${projectId}/render-final`, undefined, {
        headers: { "idempotency-key": idempotencyKey },
      }),
  });
}

/**
 * REM-021: the project's newest non-terminal (queued/running) render job, or
 * null. Used by the Render screen on mount to re-attach to an in-flight render
 * after a reload/navigation (so the user doesn't lose progress or double-queue).
 * No polling here — once it hydrates a jobId, `useJob` takes over the polling.
 */
export function useActiveJob(
  projectId: string | undefined,
  opts?: { enabled?: boolean },
) {
  return useQuery<JobAck | null>({
    queryKey: ["project", projectId, "active-job"],
    queryFn: () =>
      api.get<JobAck | null>(`/v1/projects/${projectId}/active-job`),
    enabled: !!projectId && (opts?.enabled ?? true),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useJob(
  jobId: string | undefined,
  opts?: { refetchInterval?: number | false },
) {
  // Ref survives across renders so we can detect "N polls without progress
  // change" and back off — saves ~50% of polls during the FFmpeg plateau
  // (status stays 'running' / progress stuck at 80% for 30-60s).
  const pollState = useRef({ lastSig: "", stablePolls: 0 });
  const baseInterval = opts?.refetchInterval ?? false;

  return useQuery<JobDetail>({
    queryKey: ["job", jobId],
    queryFn: () => api.get(`/v1/jobs/${jobId}`),
    enabled: !!jobId,
    // Use a function so TanStack re-evaluates after every refetch; stop
    // polling once the job hits a terminal state. Without this, fixed
    // numeric intervals can stop on their own in some focus / visibility
    // edge cases (esp. headless / unfocused windows) — see #render-bug.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data && (data.status === "success" || data.status === "failed" || data.status === "cancelled")) {
        return false;
      }
      if (baseInterval === false || !data) return baseInterval;
      const sig = `${data.status}|${data.step_name ?? ""}|${data.progress_pct ?? 0}`;
      if (sig !== pollState.current.lastSig) {
        pollState.current.lastSig = sig;
        pollState.current.stablePolls = 0;
      } else {
        pollState.current.stablePolls += 1;
      }
      return pollState.current.stablePolls >= POLL_STABLE_THRESHOLD
        ? Math.max(baseInterval, POLL_SLOW_INTERVAL_MS)
        : baseInterval;
    },
    // Keep polling even when the tab is in the background — render preview
    // is typically what the user came to the page for, and may finish while
    // they alt-tab.
    refetchIntervalInBackground: true,
  });
}

export function useCancelJob() {
  const qc = useQueryClient();
  return useMutation<JobDetail, Error, { jobId: string }>({
    mutationFn: ({ jobId }) => api.post(`/v1/jobs/${jobId}/cancel`),
    onSuccess: (_, { jobId }) =>
      qc.invalidateQueries({ queryKey: ["job", jobId] }),
  });
}

// ───────── Exports ─────────

export interface ExportItem {
  id: string;
  project_id: string;
  generation_job_id: string;
  kind: "mp4" | "thumbnail" | "srt" | "caption";
  bucket: string;
  s3_key: string;
  size_bytes: number;
  content_type: string;
  download_url: string;
  download_url_expires_at: string;
  created_at: string;
  expires_at: string;
}

export function useExports(projectId: string | undefined) {
  return useQuery<{ items: ExportItem[]; total: number }>({
    queryKey: ["exports", projectId],
    queryFn: () => api.get(`/v1/projects/${projectId}/exports`),
    enabled: !!projectId,
  });
}

export function useCreateExport(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { items: ExportItem[]; total: number },
    Error,
    {
      job_id: string;
      kinds?: Array<"mp4" | "thumbnail" | "srt" | "caption">;
    }
  >({
    mutationFn: (vars) => api.post(`/v1/projects/${projectId}/export`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["exports", projectId] }),
  });
}

// ───────── Audit logs ─────────

export interface AuditLogRow {
  id: string;
  capability: string;
  provider: string;
  model: string;
  cost_usd_estimated: string;
  latency_ms: number;
  status: "success" | "failed";
  created_at: string;
}

/** GA-W-11: offset-based infinite pagination replacing the hard-capped limit:100. */
const AUDIT_PAGE_SIZE = 50;

export function useInfiniteAuditLogs(workspaceId: string | undefined) {
  return useInfiniteQuery({
    queryKey: ["audit-logs", workspaceId] as const,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      api.get<{ items: AuditLogRow[]; total: number }>(
        `/v1/workspaces/${workspaceId}/audit-logs`,
        { limit: AUDIT_PAGE_SIZE, offset: pageParam },
      ),
    initialPageParam: 0 as number,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, p) => sum + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: !!workspaceId,
  });
}

// ───────── Consent (GA-X-01 Option B — mirror of mobile queries.ts) ─────────

export interface CurrentConsent {
  consent_version: string;
  locale: "vi" | "en";
  providers: Array<{
    name: string;
    capabilities: string[];
    data_shared: string[];
    region: string;
  }>;
  retention_days: number;
  policy_url: string;
  user_accepted_version: string | null;
  user_accepted_at: string | null;
  needs_reconsent: boolean;
}

export function useCurrentConsent() {
  return useQuery<CurrentConsent>({
    queryKey: ["consent", "current"],
    queryFn: () => api.get<CurrentConsent>("/v1/consents/current"),
  });
}
